import React, { useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useGetAgencyProfileDealerships } from '../../../api/agencies/useGetAgencyProfileDealerships'
import { ReactComponent as DealershipIcon } from '../../../images/icons/dealership_icon_profile.svg'
import { ReactComponent as ThumbsUp } from '../../../images/icons/thumbs-up.svg'
import { ReactComponent as CampaignsIcon } from '../../../images/icons/announcement_round.svg'
import { formatCurrency, formatNumber } from '../../../lib/utils/formatNumber'
import { useGetCampaignsByAgencyId } from '../../../api/agencies/useGetCampaignsByAgencyId'
import { CompactTable, StatsCard } from '@demandlocal/components'
import { createColumnHelper } from '@tanstack/react-table'
import { useGetAgencyByAgencyId } from '../../../api/agencies/useGetAgencyByAgencyId'
import { useGetAgencyProfileTotals } from '../../../api/agencies/useGetAgencyProfileTotals'
import { Pagination } from '../../../lib/types/Pagination'
import { useGetAgencyStatsByAgencyId } from '../../../api/agencies/useGetAgencyStatsByAgencyId'
import { ReactComponent as LogoSimple } from '../../../images/logo_vertical_colored.svg'
import { ReactComponent as ArrowIcon } from '../../../images/list_arrow.svg'

type ClientsRowShape = {
  id: number
  dealership: string
  demand_local_net: number
  gross_revenue: number
  campaigns: number
  campaign_types: Array<string>
  booked_impressions: number
  impressions: number
  clicks: number
  views: number
  ctr: number
  vtr: number
  delivery: number
}

type CampaignsRowShape = {
  id: number
  client_campaign_display_name: string
  demand_local_net: number
  gross_revenue: number
  product_code: string
  booked_impressions: number
  impressions: number
  clicks: number
  views: number
  ctr: number
  vtr: number
  delivery: number
}

const clientColumnHelper = createColumnHelper<ClientsRowShape>()
const campaignColumnHelper = createColumnHelper<CampaignsRowShape>()

export const AgencyOverview = (): JSX.Element => {
  const { agency_id } = useParams()
  const { data: agency } = useGetAgencyByAgencyId(agency_id)
  const { data: agencyStats, isLoading: isLoadingAgencyStats } =
    useGetAgencyStatsByAgencyId(agency_id)
  const {
    data: dealerships = {
      data: [],
      pagination: { total: 0, perPage: 10, currentPage: 1 },
    },
    isLoading: loadingDealerships,
  } = useGetAgencyProfileDealerships({
    pageIndex: 1,
    pageSize: 10,
    filters: { agency_id: Number(agency_id) ?? 0 },
  })

  const {
    data: totals = {
      data: [],
    },
    isLoading: loadingTotals,
  } = useGetAgencyProfileTotals({
    filters: { agency_id: Number(agency_id) ?? 0 },
  })

  const {
    data: campaigns = {
      data: [],
      pagination: { total: 0, perPage: 5, currentPage: 1 },
    },
    isLoading: loadingCampaigns,
  } = useGetCampaignsByAgencyId({
    pageIndex: 1,
    pageSize: 5,
    filters: { agency_id: Number(agency_id) ?? 0 },
    sorting: [{ id: 'clicks', desc: true }],
  })

  const highestClicksCampaign = useMemo(() => {
    const averageClicks = campaigns?.data.length
      ? campaigns.data?.reduce((acc, cur) => acc + cur.clicks, 0) /
        campaigns?.data?.length
      : 0
    const mostClicks = campaigns?.data.length
      ? campaigns?.data?.reduce((acc, cur) => (cur?.clicks > acc?.clicks ? cur : acc), {
          id: 0,
          client_campaign_display_name: '',
          clicks: 0,
        })
      : { id: 0, clicks: 0, client_campaign_display_name: 'N/A' }
    if (mostClicks?.clicks) {
      return {
        id: mostClicks?.id,
        client_campaign_display_name: mostClicks.client_campaign_display_name,
        clicks: Number(Math.round(mostClicks.clicks - averageClicks)),
      }
    }
    return null
  }, [campaigns?.data])

  const highestClicksDealership = useMemo(() => {
    const averageClicks = totals?.data.length
      ? totals?.data.reduce((acc, cur) => acc + cur.clicks, 0) / totals?.data.length
      : 0
    const mostClicks = totals?.data.reduce(
      (acc, cur) => (cur.clicks > acc.clicks ? cur : acc),
      { id: 0, dealership: '', clicks: 0 },
    )

    if (mostClicks?.clicks) {
      return {
        id: mostClicks.id,
        dealership: mostClicks.dealership,
        clicks: Number(Math.round(mostClicks.clicks - averageClicks)),
      }
    }
    return null
  }, [totals?.data])

  const clientsColumns = useMemo(
    () => [
      clientColumnHelper.accessor('dealership', {
        header: () => (
          <div className="flex flex-row items-center gap-2">
            <DealershipIcon className="w-10 h-10" />
            Active Clients
            <Link to="../clients" className="text-primary-persian-blue text-sm">
              See All ({dealerships?.pagination.total ?? 0})
            </Link>
          </div>
        ),
        cell: ({ getValue, row }) => (
          <Link
            className="text-primary-persian-blue font-bold text-sm"
            to={`../../dealership/${row.original.id}/profile`}>
            {getValue()}
          </Link>
        ),
      }),
      ...(agency?.show_budget_type > 0
        ? [
            clientColumnHelper.display({
              id: 'budget',
              header: 'Budget',
              cell: ({ row }) =>
                formatCurrency(
                  agency?.show_budget_type === 1
                    ? row.original.demand_local_net
                    : row.original.gross_revenue,
                ),
            }),
          ]
        : []),
      clientColumnHelper.accessor('campaigns', {
        header: 'Campaigns',
        cell: ({ getValue }) => getValue(),
      }),
      clientColumnHelper.accessor('campaign_types', {
        header: 'Campaign Types',
        cell: ({ getValue }) => (getValue() ?? []).join(', '),
      }),
      clientColumnHelper.accessor('booked_impressions', {
        header: 'Booked Impressions',
        cell: ({ getValue }) => formatNumber(getValue()),
      }),
      clientColumnHelper.accessor('impressions', {
        header: 'Impressions',
        cell: ({ getValue }) => formatNumber(getValue()),
      }),
      clientColumnHelper.accessor('clicks', {
        header: 'Clicks',
        cell: ({ getValue }) => formatNumber(getValue()),
      }),
      clientColumnHelper.accessor('views', {
        header: 'Views',
        cell: ({ getValue }) => formatNumber(getValue()),
      }),
      clientColumnHelper.accessor('ctr', {
        header: 'CTR',
        cell: ({ getValue }) => `${formatNumber(getValue())}%`,
      }),
      clientColumnHelper.accessor('vtr', {
        header: 'VTR',
        cell: ({ getValue }) => `${formatNumber(getValue())}%`,
      }),
      clientColumnHelper.accessor('delivery', {
        header: 'Delivery',
        cell: ({ getValue }) => `${formatNumber(getValue())}%`,
      }),
    ],
    [agency?.show_budget_type, dealerships?.pagination.total],
  )

  const campaignColumns = useMemo(
    () => [
      campaignColumnHelper.accessor('client_campaign_display_name', {
        header: () => (
          <div className="flex flex-row items-center gap-2">
            <CampaignsIcon className="w-10 h-10" />
            Active Campaigns
            <Link to="../campaigns" className="text-primary-persian-blue text-sm">
              See All ({campaigns?.pagination.total ?? 0})
            </Link>
          </div>
        ),
        cell: ({ getValue }) => getValue(),
      }),
      ...(agency?.show_budget_type > 0
        ? [
            campaignColumnHelper.display({
              id: 'budget',
              header: 'Budget',
              cell: ({ row }) =>
                formatCurrency(
                  agency?.show_budget_type === 1
                    ? row.original.demand_local_net
                    : row.original.gross_revenue,
                ),
            }),
          ]
        : []),
      campaignColumnHelper.accessor('product_code', {
        header: 'Campaigns',
        cell: ({ getValue }) => getValue(),
      }),
      campaignColumnHelper.accessor('booked_impressions', {
        header: 'Booked Impressions',
        cell: ({ getValue }) => formatNumber(getValue()),
      }),
      campaignColumnHelper.accessor('impressions', {
        header: 'Impressions',
        cell: ({ getValue }) => formatNumber(getValue()),
      }),
      campaignColumnHelper.accessor('clicks', {
        header: 'Clicks',
        cell: ({ getValue }) => formatNumber(getValue()),
      }),
      campaignColumnHelper.accessor('views', {
        header: 'Views',
        cell: ({ getValue }) => formatNumber(getValue()),
      }),
      campaignColumnHelper.accessor('ctr', {
        header: 'CTR',
        cell: ({ getValue }) => `${formatNumber(getValue())}%`,
      }),
      campaignColumnHelper.accessor('vtr', {
        header: 'VTR',
        cell: ({ getValue }) => `${formatNumber(getValue())}%`,
      }),
      campaignColumnHelper.accessor('delivery', {
        header: 'Delivery',
        cell: ({ getValue }) => `${formatNumber(getValue())}%`,
      }),
    ],
    [agency?.show_budget_type, campaigns?.pagination.total],
  )

  return (
    <div className={'gap-6 flex flex-col'}>
      {!isLoadingAgencyStats && !loadingDealerships && (
        <div className="flex flex-row gap-3">
          <StatsCard
            title={'Active Clients'}
            content={<span>{dealerships?.pagination.total}</span>}
            color={'bg-chrysler-blue/20'}
          />
          <StatsCard
            title={'Associated Clients'}
            content={<span>{agencyStats?.associated_dealerships_count}</span>}
            color={'bg-secondary-yellow/20'}
          />
          <StatsCard
            title={'Active Campaigns'}
            content={<span>{agencyStats?.campaigns_count}</span>}
            color={'bg-light-cyan'}
          />
          {agency?.show_budget_type > 0 ? (
            <StatsCard
              title={'Budget'}
              content={<span>{formatCurrency(agencyStats?.budget)}</span>}
              color={'bg-office-green/20'}
            />
          ) : (
            <></>
          )}
        </div>
      )}
      {!loadingTotals && (
        <div className="grid grid-cols-2 gap-5">
          <div className="flex flex-row items-center p-5 border-2 rounded-lg gap-3 bg-gainsboro-gray/20">
            <span className="font-bold text-3xl">
              {formatNumber(
                totals?.data?.reduce((acc, cur) => cur.impressions + acc, 0) ?? 0,
              )}
            </span>
            <span>Total impressions this month accross all campaigns</span>
          </div>
          <div className="flex flex-row items-center p-5 border-2 rounded-lg gap-3 bg-gainsboro-gray/20">
            <span className="font-bold text-3xl">
              {formatNumber(totals?.data?.reduce((acc, cur) => cur.clicks + acc, 0) ?? 0)}
            </span>
            <span>Total clicks this month accross all campaigns</span>
          </div>
        </div>
      )}
      <CompactTable
        loading={loadingDealerships}
        columns={clientsColumns}
        data={
          dealerships as unknown as {
            data: Array<ClientsRowShape>
            pagination: Pagination
          }
        }
      />
      {!loadingDealerships && dealerships?.data?.length > 1 && (
        <div className="flex w-full">
          {highestClicksDealership && (
            <div className="flex w-full flex-col bg-tea-green/50 rounded-lg p-1">
              <div className="flex flex-row gap-3 items-center">
                <div className="bg-tea-green/50 flex justify-center items-center rounded-full w-10 h-10">
                  <ThumbsUp className="" />
                </div>
                <span className="font-bold">
                  <Link
                    className="text-primary-persian-blue"
                    to={`../../dealership/${highestClicksDealership?.id}/profile`}>
                    {highestClicksDealership?.dealership}
                  </Link>{' '}
                  had {formatNumber(highestClicksDealership?.clicks)} more clicks than
                  your average client
                </span>
              </div>
            </div>
          )}
        </div>
      )}
      {!loadingDealerships && dealerships?.data.length === 0 && (
        <div className="flex flex-col justify-center items-center gap-5">
          <span>You have no active dealerships yet this month</span>
          <div className="flex flex-row gap-5">
            <div
              className="flex flex-col justify-start items-center p-2 gap-2 rounded-md border-[3px] border-web-gray/20 cursor-pointer"
              onClick={(e) => {
                e.preventDefault()
                window.open('mailto:support@link1data.com', '_blank')
              }}>
              <div className="flex flex-row justify-center items-center">
                <div className="flex items-center justify-center my-5 mx-auto rounded-full w-20 h-20 bg-white border-4 border-primary-persian-blue/10">
                  <LogoSimple className="object-contain px-2" />
                </div>
              </div>
              <span className="font-bold text-center">Let us Help You</span>
              <span className="text-center">
                Work with our LinkOne representative to add your dealerships
              </span>
              <ul className="p-2 *:pb-2">
                <li className="flex items-start gap-2">
                  <ArrowIcon className="h-6 w-6 mt-0.5 flex-shrink-0 " />
                  <span>
                    Have a skilled representative create your dealerships for you
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}
      <CompactTable
        loading={loadingCampaigns}
        columns={campaignColumns}
        data={
          campaigns as unknown as {
            data: Array<CampaignsRowShape>
            pagination: Pagination
          }
        }
      />
      {!loadingCampaigns && campaigns?.data?.length > 1 && (
        <div className="flex w-full">
          {highestClicksCampaign && (
            <div className="flex w-full flex-col bg-tea-green/50 rounded-lg p-1">
              <div className="flex flex-row gap-3 items-center">
                <div className="bg-tea-green/50 flex justify-center items-center rounded-full w-10 h-10">
                  <ThumbsUp className="" />
                </div>
                <span className="font-bold">
                  <Link
                    className="text-primary-persian-blue"
                    to={`../../dealership/${highestClicksCampaign?.id}/profile`}>
                    {highestClicksCampaign?.client_campaign_display_name}
                  </Link>{' '}
                  had {formatNumber(highestClicksCampaign?.clicks)} more clicks than your
                  average campaign
                </span>
              </div>
            </div>
          )}
        </div>
      )}
      {!loadingCampaigns && campaigns?.data.length === 0 && (
        <div className="flex flex-col justify-center items-center gap-5">
          <span>You have no active campaigns yet this month</span>
          <div className="flex flex-row gap-5">
            <div
              className="flex flex-col justify-start items-center p-2 gap-2 rounded-md border-[3px] border-web-gray/20 cursor-pointer"
              onClick={(e) => {
                e.preventDefault()
                window.open('mailto:support@link1data.com', '_blank')
              }}>
              <div className="flex flex-row justify-center items-center">
                <div className="flex items-center justify-center my-5 mx-auto rounded-full w-20 h-20 bg-white border-4 border-primary-persian-blue/10">
                  <LogoSimple className="object-contain px-2" />
                </div>
              </div>
              <span className="font-bold text-center">Let us Help You</span>
              <span className="text-center">
                Work with our LinkOne representative to add campaigns
              </span>
              <ul className="p-2 *:pb-2">
                <li className="flex items-start gap-2">
                  <ArrowIcon className="h-6 w-6 mt-0.5 flex-shrink-0 " />
                  <span>Have a skilled representative create your campaigns for you</span>
                </li>
                <li className="flex items-start gap-2">
                  <ArrowIcon className="h-6 w-6 mt-0.5 flex-shrink-0 " />
                  <span>
                    Target multiple different audiences and platforms with our custom
                    campaigns
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AgencyOverview
